<!-- 
Listing of notes.
-->
<template>
  <div>
    <our-loading-indicator v-if="loading"/>
    <v-container v-if="!loading" fluid class="pa-2">
      <p v-if="!anyNotes" class="ml-4 mt-4">
        This provider does not have any notes yet.
      </p>
      <v-toolbar dense flat color="white">
        <v-tooltip top>
          <v-btn-toggle 
            v-if="anyNotes"
            slot="activator" v-model="view"
            class="mb-2 mt-2 elevation-0" style="border: 1px solid #f0f0f0;" 
            mandatory
          >
            <v-btn flat color="primary">
              <v-icon>view_module</v-icon>
            </v-btn>
            <v-btn flat color="primary">
              <v-icon>list</v-icon>
            </v-btn>
          </v-btn-toggle>
          <span>Switch between card and list style view of notes.</span>
        </v-tooltip>
        <v-btn v-if="canEdit" flat color="primary" @click="$emit('click')">
          <v-icon class="mr-2">add_circle</v-icon>
          Add note
        </v-btn>
        <v-spacer/>
        <div v-if="levyCollectionDescription" style="width: 200px; padding-right: 10px; position: relative; top: 8px;">
          <v-select
            v-model="currentFilter"
            :items="filters"
            class="caption"
            label="Filter"
          />
        </div>
        <div v-if="anyNotes" style="width: 200px; padding-right: 10px; position: relative; top: 8px;">
          <v-select
            v-model="currentOrder"
            :items="sortOrders"
            class="caption"
            label="Sort order"
          />
        </div>
      </v-toolbar>
      <v-layout v-if="anyNotes" row wrap>
        <v-flex 
          v-for="note in sortedFilteredListing" :key="note.id"
          :class="view == viewType.card ? 'xs12 sm6 xl4' : 'xs12'"
        >
          <v-card flat class="ma-2 card">
            <v-card-actions class="card_actions">
              <template v-if="note.levyCollectionId">
                <template v-if="getLevyCollectionDescription(note.levyCollectionYear, note.levyCollectionType) == levyCollectionDescription">
                  <v-tooltip bottom style="cursor: help">
                    <v-icon slot="activator" color="warning">
                      how_to_vote
                    </v-icon>
                    <span>This note is for the {{ getLevyCollectionDescription(note.levyCollectionYear, note.levyCollectionType) }} levy collection.</span>
                  </v-tooltip>
                </template>
              </template>
              <span v-if="note.levyCollectionId" class="ml-2 font-weight-medium">
                {{ getLevyCollectionDescription(note.levyCollectionYear, note.levyCollectionType) }}
              </span>
              <template v-if="note.isReminderForNextYear">
                <template v-if="getLevyCollectionDescription(note.levyCollectionYear + 1, note.levyCollectionType) == levyCollectionDescription">
                  <v-tooltip bottom style="cursor: help">
                    <v-icon slot="activator" class="ml-2" color="warning">schedule</v-icon>
                    <span>This note is a reminder for the {{ note.levyCollectionYear + 1 }} {{ note.levyCollectionType }} levy collection.</span>
                  </v-tooltip>
                </template>
                <template v-else>
                  <v-tooltip v-if="note.isReminderForNextYear" bottom style="cursor: help">
                    <v-icon slot="activator" class="ml-2">schedule</v-icon>
                    <span>This note is a reminder for the {{ note.levyCollectionYear + 1 }} {{ note.levyCollectionType }} levy collection.</span>
                  </v-tooltip>
                </template>
              </template>
              <v-spacer/>
              <v-btn flat color="primary" @click="$emit('click', note.id)">
                <span v-if="canEdit">Edit</span>
                <span v-else>Open</span>
              </v-btn>
            </v-card-actions>
            <v-card-text :class="view == viewType.card ? 'card_view' : 'card_list'">
            <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component vue/no-v-html -->
              <p class="markdown-body" v-html="note.noteHtml" />
              <p class="caption mb-1">
                <template v-if="note.created == note.updated">
                  <span class="grey--text">Created: </span>
                  <span>{{ note.created | compactDateTime }}</span>
                  <span class="grey--text"> by </span>
                  <span>{{ note.createdUserName }}</span>
                </template>
                <template v-else>
                  <span class="grey--text">Updated: </span>
                  <span v-if="note.created != note.updated">{{ note.updated | compactDateTime }}</span>
                  <span class="grey--text"> by </span>
                  <span>{{ note.updatedUserName }}</span>
                </template>
              </p>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

  </div>
</template>

<script>
import _ from "underscore";
import filterMixins from "@/mixins/filter.mixins";
import * as action from "@/store/notes/action-types";
import * as mutation from "@/store/notes/mutation-types";
import * as claim from "@/store/claim-types";

const allProvider = 0;
const thisLevyCollection = 1;

const createdAsc = 0;
const createdDesc = 1;
const updatedAsc = 2;
const updatedDesc = 3;

export default {
  mixins: [filterMixins],
  props: {
    providerId: {
      required: true,
      type: Number
    },
    levyCollectionDescription: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      view: 0,
      viewType: {
        card: 0,
        list: 1
      },
      filters: [
        { value: allProvider, text: "All provider notes" },
        { value: thisLevyCollection, text: "For this levy collection" }
      ],
      currentFilter: allProvider,
      sortOrders: [
        { value: createdAsc, text: "Created ascending" },
        { value: createdDesc, text: "Created descending" },
        { value: updatedAsc, text: "Updated ascending" },
        { value: updatedDesc, text: "Updated descending" }
      ],
      currentOrder: updatedDesc
    };
  },
  computed: {
    sortedFilteredListing() {
      var list;
      // Sorting
      list = _.sortBy(this.listing.data, note => {
        return this.currentOrder == createdAsc || this.currentOrder == createdDesc ? note.created : note.updated;
      });
      if (this.currentOrder == createdDesc || this.currentOrder == updatedDesc) {
        list = list.reverse();
      }
      // Filtering
      if (this.currentFilter == thisLevyCollection) {
        list = list.filter(
          note =>
            this.getLevyCollectionDescription(note.levyCollectionYear, note.levyCollectionType) ==
              this.levyCollectionDescription ||
            (note.isReminderForNextYear &&
              this.getLevyCollectionDescription(note.levyCollectionYear + 1, note.levyCollectionType) ==
                this.levyCollectionDescription)
        );
      }
      return list;
    },
    listing() {
      return this.$store.state.notes.listing;
    },
    loading() {
      return this.listing.loading;
    },
    loaded() {
      return this.listing.loaded;
    },
    anyNotes() {
      return this.loaded && this.listing.data.length > 0;
    },
    canEdit() {
      const hasPermission = this.$store.getters.claims.includes(claim.PROVIDER_NOTES_EDIT);
      return hasPermission;
    }
  },
  watch: {
    providerId() {
      this.refresh();
    },
    loaded() {
      this.refresh();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    getLevyCollectionDescription(year, type) {
      return year + " " + type;
    },
    async refresh() {
      const needsReloading =
        (!this.listing.loaded && !this.listing.loading) || this.listing.providerId != this.providerId;
      if (needsReloading) {
        this.$store.commit(mutation.LIST_SET_FILTERS, {
          providerId: this.providerId
        });
        this.$store.dispatch(action.LIST);
      }
    }
  }
};
</script>

<style lang="scss">
.card {
  border: 1px solid #e8e8e8 !important;
  border-radius: 5px !important;
}
.card_view {
  height: 220px;
  overflow-y: auto;
}
.card_actions {
  cursor: default;
}
.markdown-body {
  font-size: 13px !important;
}
</style>